import React from "react";
import { Icon } from "@iconify/react";

const Pagination = ({ currentPage, pageSize, totalItems, onPageChange }) => {
  // Calculate total number of pages
  const totalPages = Math.ceil(totalItems / pageSize);

  // If no pages, don't render pagination
  if (totalPages <= 1) {
    return null;
  }

  // Function to generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const showEllipsisStart = currentPage > 4;
    const showEllipsisEnd = currentPage < totalPages - 3;

    // Always show first page
    pages.push(1);

    if (totalPages <= 7) {
      // If 7 or fewer pages, show all
      for (let i = 2; i < totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show ellipsis and smart page range
      if (showEllipsisStart) {
        // Show first 2 pages
        if (currentPage > 5) {
          pages.push(2);
          pages.push("ellipsis-start");
        } else {
          pages.push(2);
          pages.push(3);
        }
      } else {
        // Show pages 2 and 3
        pages.push(2);
        pages.push(3);
      }

      // Show current page and neighbors
      if (currentPage > 3 && currentPage < totalPages - 2) {
        // Only add if not already included
        if (currentPage - 1 > 3) {
          pages.push(currentPage - 1);
        }
        if (currentPage !== 2 && currentPage !== 3) {
          pages.push(currentPage);
        }
        if (currentPage + 1 < totalPages - 2) {
          pages.push(currentPage + 1);
        }
      }

      if (showEllipsisEnd) {
        // Show last 2 pages
        if (currentPage < totalPages - 4) {
          pages.push("ellipsis-end");
          pages.push(totalPages - 1);
        } else {
          pages.push(totalPages - 2);
          pages.push(totalPages - 1);
        }
      } else {
        // Show second-to-last and third-to-last pages
        if (!pages.includes(totalPages - 2)) {
          pages.push(totalPages - 2);
        }
        if (!pages.includes(totalPages - 1)) {
          pages.push(totalPages - 1);
        }
      }
    }

    // Always show last page
    if (!pages.includes(totalPages)) {
      pages.push(totalPages);
    }

    // Remove duplicates and sort
    return [...new Set(pages)];
  };

  const pageNumbers = getPageNumbers();

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      onPageChange(page);
    }
  };

  return (
    <div className="pagination-wrapper">
      <ul className="pagination">
        {/* First Page Button */}
        <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
          <button
            className="page-link"
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            title="First Page"
          >
            <Icon icon="mdi:chevron-double-left" fontSize={16} />
          </button>
        </li>

        {/* Previous Page Button */}
        <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
          <button
            className="page-link"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            title="Previous Page"
          >
            <Icon icon="mdi:chevron-left" fontSize={16} />
          </button>
        </li>

        {/* Page Numbers */}
        {pageNumbers.map((page, index) => {
          if (page === "ellipsis-start" || page === "ellipsis-end") {
            return (
              <li key={`ellipsis-${index}`} className="page-item disabled">
                <span className="page-link">...</span>
              </li>
            );
          }

          return (
            <li
              key={page}
              className={`page-item ${currentPage === page ? "active" : ""}`}
            >
              <button
                className="page-link"
                onClick={() => handlePageChange(page)}
              >
                {page}
              </button>
            </li>
          );
        })}

        {/* Next Page Button */}
        <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
          <button
            className="page-link"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            title="Next Page"
          >
            <Icon icon="mdi:chevron-right" fontSize={16} />
          </button>
        </li>

        {/* Last Page Button */}
        <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
          <button
            className="page-link"
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
            title="Last Page"
          >
            <Icon icon="mdi:chevron-double-right" fontSize={16} />
          </button>
        </li>
      </ul>

      {/* Page Info */}
      <div className="pagination-info">
        Page {currentPage} of {totalPages}
      </div>
    </div>
  );
};

export default Pagination;
